<template>
  <div class="ag-theme-alpine render-list mt-2">
    <ag-grid-vue
      v-if="rowData"
      class="ag-theme-alpine render"
      style="width: 100%; height: 100%"
      :gridOptions="gridOptions"
      :getRowId="getRowId"
      :domLayout="'normal'"
      :enableFilter="true"
      :enableColResize="true"
      :rowSelection="'multiple'"
      :enableRangeSelection="true"
      @grid-ready="onGridReady"
      @selection-changed="onSelectionChanged"
      :columnDefs="columnDefs"
      :rowData="rowData"
    ></ag-grid-vue>

    <!-- pagination -->
    <v-container
      v-if="rowData"
      class="custom-pagination ma-0"
      :style="{
        height: isMobile ? '110px' : '50px',
        'flex-direction': isMobile ? 'column' : '',
        'align-items': isMobile ? 'flex-start' : '',
        padding: isMobile ? '17px 10px !important' : '5px !important',
      }"
    >
      <div
        class="d-flex"
        :class="{ 'gap-7': !isMobile, 'justify-space-between w-100': isMobile }"
      >
        <v-card-text class="pa-0 text-body-2 d-flex gap-1 mt-1">
          <span class="txt">{{ $t("show") }}</span>
          <span>{{ startRow }}</span>
          <span class="txt">{{ $t("until") }}</span>
          <span>{{ endRow }}</span>
          <span class="txt">{{ $t("of") }}</span>
          <span>{{ totalRows }}</span>
        </v-card-text>
        <v-text-field
          v-if="!isMobile"
          v-model.number="currentPage"
          :label="$t('goToPage')"
          min="1"
          :max="lastPage"
          type="number"
          outlined
          height="26"
          style="width: 95px; margin-top: 2px"
          hide-details
          hide-spin-buttons
          dense
          :suffix="'  /  ' + lastPage"
          @input="onPageInput"
        />
        <div class="d-flex gap-2">
          <v-card-text class="pa-0 text-caption txt mt-1">
            {{ $t("records-in-page") }} :
          </v-card-text>
          <v-select
            dense
            outlined
            class="page-size-select mt-2"
            :items="[10, 25, 50, 100]"
            v-model="pageSize"
            @change="onPageSizeChange"
            style="width: 70px"
            height="26"
          ></v-select>
        </div>
      </div>
      <v-container
        class="d-flex align-center pa-0 ma-0"
        style="gap: 0.2rem"
        :class="{ 'justify-center': isMobile }"
        :style="{ width: isMobile ? '100%' : 'auto' }"
      >
        <v-btn outlined height="26" @click="goToFirst" :disabled="currentPage === 1">
          <v-icon size="16">mdi-chevron-double-right</v-icon>
        </v-btn>
        <v-btn outlined height="26" @click="goToPrev" :disabled="currentPage === 1">
          <v-icon size="16">mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn
          v-for="(page, index) in pageNumbers"
          :key="index"
          outlined
          height="26"
          :disabled="page === '...'"
          :color="currentPage === page ? 'primary' : ''"
          @click="page !== '...' && goToPage(page)"
        >
          {{ page }}
        </v-btn>
        <v-btn
          outlined
          height="26"
          @click="goToNext"
          :disabled="currentPage === lastPage"
        >
          <v-icon size="16">mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn
          outlined
          height="26"
          @click="goToLast"
          :disabled="currentPage === lastPage"
        >
          <v-icon size="16">mdi-chevron-double-left</v-icon>
        </v-btn>
      </v-container>
    </v-container>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import Vue from "vue";
import AgVMenu from "@/views/dashboard/components/general/AgGridComponents/Ag-vmenu.vue";

export default {
  props: {
    columnDefs: Array,
    rowData: Array,
    totalRows: Number,
    lastPage: Number,
    loading: Boolean,
  },
  data() {
    return {
      startRow: 0,
      endRow: 0,
      pageSize: 10,
      currentPage: 1,
      gridApi: null,
      gridColumnApi: null,
      gridOptions: {
        enableRtl: true,
        pagination: true,
        headerHeight: 37,
        groupSelectsFiltered: true,
        suppressMenuHide: false,
        suppressRowClickSelection: false,
        getRowHeight: () => 60,
        rowHoverHighlight: false,
        suppressPaginationPanel: true,
        suppressKeyboardEvent: (params) => {
          const e = params.event;
          const key = e.key;

          const isEditingAutoComplete =
            params.node?.editing &&
            params.api
              ?.getEditingCells?.()
              ?.some(
                (cell) =>
                  cell.rowIndex === params.node.rowIndex &&
                  cell.column.getColId() === "given"
              );

          if (isEditingAutoComplete) {
            if (["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(key)) {
              return true;
            }
          }

          return false;
        },
        icons: {
          menu: `
            <span 
              class="v-icon notranslate mdi mdi-filter-outline theme--light" 
              aria-hidden="true"
              style="font-size: 16px;"
            >
            </span>
          `,
          sortAscending: `
            <span 
              class="v-icon notranslate mdi mdi-arrow-up theme--light" 
              aria-hidden="true"
              style="font-size: 16px;"
            >
            </span>
          `,
          sortDescending: `
            <span 
              class="v-icon notranslate mdi mdi-arrow-down theme--light" 
              aria-hidden="true"
              style="font-size: 16px;"
            >
            </span>
          `,
          smallDown: `
            <svg width="16" height="16">
              <path d="M7 10l5 5 5-5z" />
            </svg>
          `,
        },
        localeText: {
          applyFilter: this.$t("perform"),
          resetFilter: this.$t("reset"),
          cancel: this.$t("cancel"),
          clearFilter: this.$t("clear"),

          equals: this.$t("equals"),
          notEqual: this.$t("not-equals"),
          greaterThan: this.$t("biger-than"),
          greaterThanOrEqual: this.$t("biger-equals"),
          lessThan: this.$t("smaller-than"),
          lessThanOrEqual: this.$t("smaller-equals"),
          inRange: this.$t("between"),

          contains: this.$t("include"),
          notContains: this.$t("not-include"),
          startsWith: this.$t("start-with"),
          endsWith: this.$t("end-with"),

          filterOoo: this.$t("enter-filter"),
          blank: this.$t("blank"),
          notBlank: this.$t("not-blank"),
          noRowsToShow: this.$t("nothing-for-render"),

          columns: this.$t("columns"),
          filters: this.$t("filters"),
          sortAscending: this.$t("sorting-asc"),
          sortDescending: this.$t("sorting-des"),

          filterValuePlaceholder: this.$t("filter-value"),

          andCondition: this.$t("and"),
          orCondition: this.$t("or"),
        },
        overlayLoadingTemplate: `
          <div class="ag-overlay-loading-center custom-loading">
            <div class="spinner"></div>
          </div>
        `,
        overlayNoRowsTemplate: `
          <div class="ag-overlay-no-rows-center custom-no-rows">
            هیچ رکوردی یافت نشد!
          </div>
        `,
      },
    };
  },
  components: {
    AgGridVue,
    AgVMenu: Vue.extend(AgVMenu),
    CustomHeaderCheckbox: {
      template: `
        <div style="display:flex;align-items:center;justify-content:center;">
          <input type="checkbox" @change="onToggle" ref="check" />
        </div>
      `,
      methods: {
        onToggle(e) {
          const checked = e.target.checked;
          const api = this.params.api;

          const currentPage = this.currentPage;
          const pageSize = api.paginationGetPageSize();

          const start = currentPage * pageSize;
          const end = start + pageSize;

          api.forEachNode((node, index) => {
            if (index >= start && index < end) node.setSelected(checked);
          });
        },
      },
    },
  },
  computed: {
    pageNumbers() {
      const total = this.lastPage;
      const current = this.currentPage;
      const window = 5;

      if (total <= window) return Array.from({ length: total }, (_, i) => i + 1);

      const pages = [1];

      if (current > 3) pages.push("...");

      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);

      for (let p = start; p <= end; p++) pages.push(p);

      if (current < total - 2) pages.push("...");
      pages.push(total);

      return pages;
    },
  },
  mounted() {
    document.addEventListener("keydown", this.globalKeyDown);
  },
  methods: {
    getRowId(params) {
      return params.data.id;
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;

      if (this.loading) {
        this.gridApi.showLoadingOverlay();
      } else {
        this.gridApi.hideOverlay();
      }

      this.setInitialFocus();
      this.updatePagination();
    },
    globalKeyDown(event) {
      const api = this.gridApi;
      if (!api) return;

      if (api.getEditingCells && api.getEditingCells().length > 0) return;

      const key = event.key;

      if (["PageDown", "ArrowDown"].includes(key)) {
        event.preventDefault();
        this.navigateRow(api, 1);
      }

      if (["PageUp", "ArrowUp"].includes(key)) {
        event.preventDefault();
        this.navigateRow(api, -1);
      }
    },
    navigateRow(api, direction) {
      const selected = api.getSelectedNodes();
      const total = api.getDisplayedRowCount();

      let nextIndex = 0;
      let currentColumn = 0;

      if (selected.length) {
        const currentIndex = selected[0].rowIndex;
        currentColumn = api.getFocusedCell()?.column?.getColId();
        nextIndex = currentIndex + direction;
      }

      nextIndex = Math.max(0, Math.min(nextIndex, total - 1));

      api.forEachNode((node) => node.setSelected(false));

      const target = api.getDisplayedRowAtIndex(nextIndex);
      if (target) {
        target.setSelected(true);
        api.ensureIndexVisible(nextIndex);
        api.setFocusedCell(nextIndex, currentColumn);
      }
    },
    setInitialFocus() {
      const api = this.gridApi;
      if (api && api.getDisplayedRowCount() > 0) {
        api.setFocusedCell(0, this.columnDefs[0].field);
        api.getDisplayedRowAtIndex(0).setSelected(true);
      }
    },
    onPageSizeChange(size) {
      if (!this.gridApi) return;
      this.pageSize = size;
      this.currentPage = 1;
      this.$emit("updateItemPerPage", size);
      this.updatePagination();
    },
    goToPage(page) {
      if (!this.gridApi) return;
      this.currentPage = page;
      this.$emit("updatePagination", this.currentPage);
      this.updatePagination();
    },
    goToFirst() {
      if (!this.gridApi) return;
      this.currentPage = 1;
      this.$emit("updatePagination", this.currentPage);
      this.updatePagination();
    },
    goToPrev() {
      if (!this.gridApi) return;
      this.currentPage -= 1;
      this.$emit("updatePagination", this.currentPage);
      this.updatePagination();
    },
    goToNext() {
      if (!this.gridApi) return;
      this.currentPage += 1;
      this.$emit("updatePagination", this.currentPage);
      this.updatePagination();
    },
    goToLast() {
      if (!this.gridApi) return;
      this.currentPage = this.lastPage;
      this.$emit("updatePagination", this.currentPage);
      this.updatePagination();
    },
    updatePagination() {
      const checkbox = document.getElementById("customHeaderCheckbox");
      if (checkbox) checkbox.checked = false;
      this.startRow = (this.currentPage - 1) * this.pageSize + 1;
      this.endRow = Math.min(this.startRow + this.pageSize - 1, this.totalRows);
    },
    onSelectionChanged() {
      const selected = this.gridApi.getSelectedRows();
      this.$emit("selection-changed", selected);
    },
    onPageInput(value) {
      if (value === "" || value == null) return;
      const num = Number(value);
      if (isNaN(num)) return;

      if (num < 1 || num > this.lastPage) return;

      this.$emit("updatePagination", num);
    },
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.globalKeyDown);
  },
  watch: {
    loading(val) {
      if (!this.gridApi) return;
      if (val) {
        this.gridApi.showLoadingOverlay();
      } else {
        if (this.rowData && this.rowData.length > 0) {
          this.gridApi.hideOverlay();
        } else {
          this.gridApi.showNoRowsOverlay();
        }
      }
    },
  },
};
</script>

<style>
.ag-theme-alpine.render-list {
  flex: 1;
  min-height: 0;
  position: relative;
}
.ag-theme-alpine.render-list .custom-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  bottom: 0;
  width: 100%;
  background: #fff;
  border: 1px solid silver;
  padding: 5px !important;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}
.ag-theme-alpine.render-list .custom-pagination .v-select {
  position: relative;
  bottom: 7px;
  width: 65px;
}
.ag-theme-alpine.render-list .custom-pagination .txt {
  color: #a6a6a6;
}
.ag-theme-alpine.render-list
  .custom-pagination
  .v-btn:not(.v-btn--round).v-size--default {
  min-width: 26px;
  padding: 5px !important;
}
.ag-theme-alpine.render-list .v-btn.v-size--default {
  font-size: 8pt;
}
.v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined
  .v-input__append-inner {
  margin-top: 2px;
}
.v-label.v-label {
  font-size: 13px;
}
.ag-theme-alpine.render-list .ag-body.ag-layout-normal {
  height: calc(100% - 97px);
}
.ag-theme-alpine.render-list .ag-header-cell-text {
  font-size: 8.5pt;
}
.ag-theme-alpine.render-list .ag-body {
  flex: none;
}
.ag-theme-alpine.render-list
  .ag-pinned-right-cols-container
  .ag-row
  .ag-cell:nth-child(2) {
  text-align: center;
}
.ag-theme-alpine.render-list .ag-row .ag-cell {
  display: flex;
  align-items: center;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  line-height: 20px;
}
.ag-theme-alpine.render-list .ag-header {
  /* height: 45px !important;
    min-height: 45px !important; */
  background-color: #c0c0c06e !important;
  /* border-radius: 4px; */
  border-bottom: none;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-body-vertical-scroll
  ::-webkit-scrollbar {
  width: 7px;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-body-vertical-scroll
  ::-webkit-scrollbar-track {
  background: #f1f0f0;
  border-radius: 6px;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-body-vertical-scroll
  ::-webkit-scrollbar-thumb {
  background: #c0c0c06e;
  border-radius: 6px;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-body-vertical-scroll
  ::-webkit-scrollbar-thumb:hover {
  background: #9e9e9ecf;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-body-horizontal-scroll
  ::-webkit-scrollbar {
  height: 7px;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-body-horizontal-scroll
  ::-webkit-scrollbar-track {
  background: #f1f0f0;
  border-radius: 6px;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-body-horizontal-scroll
  ::-webkit-scrollbar-thumb {
  background: #c0c0c06e;
  border-radius: 6px;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-body-horizontal-scroll
  ::-webkit-scrollbar-thumb:hover {
  background: #9e9e9ecf;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-root.ag-unselectable.ag-layout-normal {
  padding: 0 0 5px 0;
}
.ag-theme-alpine.render-list .ag-rtl .ag-selection-checkbox {
  margin-left: 5px;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-input-field-input.ag-checkbox-input {
  cursor: pointer;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-wrapper.ag-input-wrapper.ag-checkbox-input-wrapper.ag-checked {
  background-color: transparent;
}
/* .ag-theme-alpine.render-list .ag-theme-alpine.render .ag-pinned-right-header {
  border: none;
} */
.ag-theme-alpine.render-list .ag-row {
  font-size: 11px !important;
}
.ag-theme-alpine.render-list .ag-theme-alpine.render .ag-row-selected,
.ag-row-selected::before {
  background-color: rgb(169 217 255 / 12%);
}
.ag-theme-alpine.render-list .ag-theme-alpine.render .ag-row-hover,
.ag-theme-alpine.render-list .ag-theme-alpine.render .ag-row-hover::after,
.ag-theme-alpine.render-list .ag-theme-alpine.render .ag-row-hover::before {
  background-color: rgb(169 217 255 / 12%) !important;
}
.ag-theme-alpine.render-list
  .ag-theme-alpine.render
  .ag-row-hover.ag-row-selected::before {
  background-image: none !important;
}
.ag-theme-alpine.render-list
  .ag-header-cell-menu-button:not(.ag-header-menu-always-show) {
  transition: opacity 0.2s;
  opacity: 1;
}
.custom-pagination
  .v-text-field--outlined.v-input--dense.v-text-field--outlined
  > .v-input__control
  > .v-input__slot {
  min-height: 25px;
}
/* .ag-sort-indicator-icon.ag-sort-ascending-icon.ag-hidden,
.ag-sort-indicator-icon.ag-sort-descending-icon.ag-hidden {
  display: block !important;
}
.ag-sort-indicator-icon.ag-sort-descending-icon.ag-hidden {
  padding-right: 0;
} */
.ag-theme-alpine.render-list .ag-header-icon.ag-header-label-icon.ag-filter-icon {
  display: none;
}
.ag-theme-alpine.render-list .ag-theme-alpine .ag-filter-apply-panel {
  padding: 6px 12px;
}
.ag-theme-alpine.render-list .ag-filter-apply-panel {
  justify-content: space-between;
}
.ag-theme-alpine.render-list .ag-theme-alpine .ag-rtl .ag-filter-apply-panel-button {
  margin-right: 0;
}
.ag-theme-alpine.render-list .ag-theme-alpine .ag-standard-button {
  padding: 3px 13px;
}
.ag-theme-alpine.render-list .ag-standard-button {
  font-size: 8pt;
}
.ag-theme-alpine.render-list .ag-header-cell-resize::after {
  background-color: silver;
}
.ag-theme-alpine.render-list .ag-overlay-loading-center {
  background-color: transparent;
  border: none;
  box-shadow: none;
}
.ag-theme-alpine.render-list .ag-overlay {
  height: calc(100% - 91px);
  margin-top: 43px;
}
.ag-theme-alpine.render-list .ag-pinned-right-header {
  border: none;
}
.ag-theme-alpine.render-list
  .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left):not(.ag-cell-range-single-cell) {
  border-left: none;
}
.ag-theme-alpine.render-list
  .ag-pinned-right-cols-container
  .ag-cell-value.ag-cell.ag-cell-not-inline-editing.ag-cell-first-right-pinned {
  justify-content: center;
  padding: 0;
}
.ag-theme-alpine.render-list
  .ag-pinned-right-cols-container
  .ag-cell-value.ag-cell.ag-cell-not-inline-editing.ag-cell-focus {
  border-left: 1px solid #2196f3;
}
/* .ag-theme-alpine.render-list .ag-overlay-loading-wrapper {
  background-color: #0000001c;
} */
.spinner {
  border: 4px solid silver;
  border-top: 4px solid #0066ff;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 10px auto;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.page-size-select .v-input__control {
  min-height: 32px !important;
}
.page-size-select .v-input__slot {
  padding: 0 8px !important;
}
.page-size-select .v-select__selections {
  text-align: center !important;
}
</style>
